'use client';

import { useCallback, useLayoutEffect, useState } from 'react';

import { slugifyBlogHeadingForId } from '@/app/_utils/blog-heading-anchor';

const TOC_HEADING_SELECTOR = '.article-content .blog-article-html h2';
const MIN_HEADINGS_FOR_TOC = 2;

type TTocItem = {
  readonly id: string;
  readonly label: string;
};

interface IBlogTableOfContentsProps {
  readonly contentKey: string;
}

const collectTocItems = (): TTocItem[] => {
  const headings = Array.from(document.querySelectorAll<HTMLHeadingElement>(TOC_HEADING_SELECTOR));
  if (headings.length < MIN_HEADINGS_FOR_TOC) return [];

  const used = new Set<string>();
  return headings.map((el, index) => {
    const label = el.textContent?.trim() || `Section ${index + 1}`;
    let id = el.id.trim();

    if (id && used.has(id)) {
      id = '';
    }

    if (!id) {
      const base = slugifyBlogHeadingForId(label);
      let candidate = base;
      let suffix = 2;
      while (used.has(candidate)) {
        candidate = `${base}-${suffix}`;
        suffix += 1;
      }
      id = candidate;
      el.id = id;
    }

    used.add(id);
    return { id, label };
  });
};

const BlogTableOfContents = ({ contentKey }: IBlogTableOfContentsProps) => {
  const [items, setItems] = useState<TTocItem[]>([]);

  useLayoutEffect(() => {
    // DOM fragment ids: must run after .blog-article-html mounts; sync read avoids TOC layout flash.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- TOC is derived from rendered HTML, not React state graph
    setItems(collectTocItems());
  }, [contentKey]);

  const scrollToId = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }, []);

  if (items.length < MIN_HEADINGS_FOR_TOC) return null;

  const list = (
    <ol className='blog-toc-list m-0 list-none space-y-1.5 p-0'>
      {items.map((item) => (
        <li key={item.id}>
          <button
            type='button'
            onClick={() => scrollToId(item.id)}
            className='w-full cursor-pointer rounded-md border border-transparent px-2 py-2 text-left text-sm leading-snug text-muted-foreground transition-all duration-200 hover:border-accent/35 hover:bg-accent/10 hover:text-accent hover:shadow-sm active:scale-[0.99] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'
          >
            {item.label}
          </button>
        </li>
      ))}
    </ol>
  );

  return (
    <div className='mb-8 md:mb-10'>
      <details className='blog-toc-details rounded-xl border border-border/50 bg-muted/15'>
        <summary className='cursor-pointer list-none px-4 py-3 text-xs font-semibold uppercase tracking-widest text-foreground transition-colors hover:bg-accent/10 hover:text-accent md:px-5 md:py-3.5 [&::-webkit-details-marker]:hidden'>
          On this page
        </summary>
        <nav aria-label='On this page' className='border-t border-border/40 px-3 pb-3 pt-1 md:px-4 md:pb-4'>
          {list}
        </nav>
      </details>
    </div>
  );
};

export default BlogTableOfContents;
